import { useCallback, useEffect, useRef, useState } from "react";

import { useAuth } from "@/modules/auth/hooks/useAuth";
import { PaymentsPayoutService } from "@/modules/dashboard/dispersions/services/paymentsPayout.service";
import type { PayoutStatus } from "@/modules/dashboard/dispersions/services/paymentsPayout.service";
import { PaymentsPayinService } from "@/modules/dashboard/payins/services/paymentsPayin.service";
import type { PayinRuntimeStatus } from "@/modules/dashboard/payins/services/paymentsPayin.service";
import { SwapService } from "@/modules/dashboard/swaps/services/swap.service";
import type { NativeSwap } from "@/modules/dashboard/swaps/services/swap.service";
import { HttpApiService } from "@/shared/services/httpApi.service";

import { LEDGER_PICKER_PAGE_SIZE } from "../constants/paymentsLedger.constants";
import type {
  LedgerOperationCandidate,
  LedgerOperationKind,
  LedgerPickerFilters,
} from "../types/paymentsLedger.types";
import { ledgerAccessLatch } from "../utils/ledgerAccessLatch";
import { payinToCandidate, payoutToCandidate, swapToCandidate } from "../utils/ledgerCandidates";

const INITIAL_FILTERS: LedgerPickerFilters = {
  organizationId: null,
  status: null,
  country: null,
  rangeDays: null,
};

export interface UseLedgerOperationPickerResult {
  kind: LedgerOperationKind;
  setKind: (kind: LedgerOperationKind) => void;
  filters: LedgerPickerFilters;
  setOrganizationId: (organizationId: string | null) => void;
  setStatus: (status: string | null) => void;
  setCountry: (country: string | null) => void;
  setRangeDays: (days: number | null) => void;
  clearFilters: () => void;
  hasActiveFilters: boolean;
  /** 1-indexed page over the offset pagination (`skip = (page - 1) * limit`). */
  page: number;
  setPage: (page: number) => void;
  pageSize: number;
  total: number;
  candidates: LedgerOperationCandidate[];
  isLoading: boolean;
  isRefreshing: boolean;
  hasError: boolean;
  hasAccess: boolean;
  refresh: () => Promise<void>;
}

/**
 * The operation explorer behind the ledger view.
 *
 * The ledger endpoint takes an `operation_id` and nothing else — there is no way to list journal entries by
 * account, by date or at all — so browsing has to start from the operation. It reads the three runtime list
 * endpoints (payouts / payins / swaps), each with its own status vocabulary, and normalizes their rows.
 *
 * No polling: the explorer is a lookup tool, and the ledger pane already refreshes the operation that is
 * open. Filters are the server-side ones only; the swaps endpoint has no `country`, so that filter does not
 * apply there and the UI hides it.
 */
export const useLedgerOperationPicker = (
  initialKind: LedgerOperationKind = "payouts",
): UseLedgerOperationPickerResult => {
  const { user } = useAuth();
  const deniedKey = user?.id ?? "anon";

  // Initial only: arriving by deep link should open the tab the operation belongs to, but a later navigation
  // inside the page must not fight the tab the user picked.
  const [kind, setKindState] = useState<LedgerOperationKind>(initialKind);
  const [filters, setFilters] = useState<LedgerPickerFilters>(INITIAL_FILTERS);
  const [page, setPageState] = useState(1);

  const [candidates, setCandidates] = useState<LedgerOperationCandidate[]>([]);
  const [total, setTotal] = useState(0);
  const [settledQueryKey, setSettledQueryKey] = useState<string | null>(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [hasError, setHasError] = useState(false);
  // Read through the latch, never copied into state: the copy would keep this pane loading forever if
  // another consumer latched first, and would freeze whatever `deniedKey` the first render happened to see.
  const hasAccess = !ledgerAccessLatch.useDenied(deniedKey);

  const requestSeq = useRef(0);

  const queryKey = [
    kind,
    filters.organizationId ?? "*",
    filters.status ?? "*",
    filters.country ?? "*",
    filters.rangeDays ?? "*",
    page,
  ].join("|");
  const isLoading = hasAccess && settledQueryKey !== queryKey;

  const fetchPage = useCallback(
    async (options: { background: boolean }) => {
      if (ledgerAccessLatch.isDenied(deniedKey)) return;
      const key = [
        kind,
        filters.organizationId ?? "*",
        filters.status ?? "*",
        filters.country ?? "*",
        filters.rangeDays ?? "*",
        page,
      ].join("|");
      const seq = ++requestSeq.current;
      if (options.background) setIsRefreshing(true);
      else setHasError(false);

      const from = filters.rangeDays
        ? new Date(Date.now() - filters.rangeDays * 24 * 60 * 60 * 1000).toISOString()
        : undefined;
      const common = {
        // Null lists cross-org (INTERNAL staff); an org member is pinned server-side either way.
        organizationId: filters.organizationId,
        from,
        limit: LEDGER_PICKER_PAGE_SIZE,
        skip: (page - 1) * LEDGER_PICKER_PAGE_SIZE,
      };

      try {
        let next: { rows: LedgerOperationCandidate[]; total: number };
        if (kind === "payouts") {
          const response = await PaymentsPayoutService.listPayouts({
            ...common,
            status: (filters.status as PayoutStatus | null) ?? undefined,
            country: filters.country ?? undefined,
          });
          next = { rows: response.data.data.map(payoutToCandidate), total: response.data.total };
        } else if (kind === "payins") {
          const response = await PaymentsPayinService.listPayins({
            ...common,
            status: (filters.status as PayinRuntimeStatus | null) ?? undefined,
            country: filters.country ?? undefined,
          });
          next = { rows: response.data.data.map(payinToCandidate), total: response.data.total };
        } else {
          // Swaps have no corridor: `country` is not part of `SwapsQueryDto` and sending it would 400
          // (`forbidNonWhitelisted`).
          const response = await SwapService.listSwaps({
            ...common,
            status: (filters.status as NativeSwap["status"] | null) ?? undefined,
          });
          next = { rows: response.data.data.map(swapToCandidate), total: response.data.total };
        }

        if (seq !== requestSeq.current) return;
        setCandidates(next.rows);
        setTotal(next.total);
        setHasError(false);
        setSettledQueryKey(key);
      } catch (err: unknown) {
        if (seq !== requestSeq.current) return;
        if (HttpApiService.isAccessDeniedError(err)) {
          ledgerAccessLatch.latch(deniedKey);
        } else if (!options.background) {
          setHasError(true);
          setCandidates([]);
          setTotal(0);
          setSettledQueryKey(key);
        }
      } finally {
        if (seq === requestSeq.current) setIsRefreshing(false);
      }
    },
    [deniedKey, filters, kind, page],
  );

  useEffect(() => {
    void fetchPage({ background: false });
  }, [fetchPage]);

  // Keep the page in range when the dataset shrinks under us.
  useEffect(() => {
    const maxPage = Math.max(1, Math.ceil(total / LEDGER_PICKER_PAGE_SIZE));
    if (page > maxPage) setPageState(maxPage);
  }, [page, total]);

  const setKind = useCallback((next: LedgerOperationKind) => {
    setKindState(next);
    // The three status vocabularies are different, and swaps have no corridor — a carried-over value would be
    // rejected by the API.
    setFilters((prev) => ({ ...prev, status: null, country: null }));
    setPageState(1);
  }, []);

  const setOrganizationId = useCallback((organizationId: string | null) => {
    setFilters((prev) => ({ ...prev, organizationId }));
    setPageState(1);
  }, []);
  const setStatus = useCallback((status: string | null) => {
    setFilters((prev) => ({ ...prev, status }));
    setPageState(1);
  }, []);
  const setCountry = useCallback((country: string | null) => {
    setFilters((prev) => ({ ...prev, country }));
    setPageState(1);
  }, []);
  const setRangeDays = useCallback((rangeDays: number | null) => {
    setFilters((prev) => ({ ...prev, rangeDays }));
    setPageState(1);
  }, []);
  const clearFilters = useCallback(() => {
    setFilters(INITIAL_FILTERS);
    setPageState(1);
  }, []);

  const refresh = useCallback(() => fetchPage({ background: true }), [fetchPage]);

  return {
    kind,
    setKind,
    filters,
    setOrganizationId,
    setStatus,
    setCountry,
    setRangeDays,
    clearFilters,
    hasActiveFilters:
      filters.organizationId !== null ||
      filters.status !== null ||
      filters.country !== null ||
      filters.rangeDays !== null,
    page,
    setPage: setPageState,
    pageSize: LEDGER_PICKER_PAGE_SIZE,
    total,
    candidates,
    isLoading,
    isRefreshing,
    hasError,
    hasAccess,
    refresh,
  };
};
